import { useCallback, useEffect, useState } from 'react';
import type { Cancelacion, Evento, ResumenInvitados, TipoEntrada } from '../../api/ops/eventos';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { PanelLateral } from '../../componentes/PanelLateral';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import type { PropsEventos } from './Eventos';
import { Cancelar, CambiarFecha, Informe } from './FechaCancelar';
import { cambiosDe, erroresForm, formDe, type FormEvento } from './formulario';
import { Datos, Entradas, Puerta, Publicar } from './Pestanas';
import { AYUDA, editable, ESTADO_EVENTO, erroresDeCampo, PESTANAS, pestanasDe, rangoEn, textoErrorGuardar, type PermisosEventos, type Pestana } from './reglas';
import { FormTipo } from './TiposEntrada';

/**
 * Ficha del evento (10.2 a 10.5) en RV Panel: pestañas Datos · Entradas · Puerta · Publicar · Fecha · Cancelar (Informe si
 * está cancelado), según el estado y los permisos (`pestanasDe`). Datos, Entradas y Puerta se guardan juntos con
 * `updateEvent` e `If-Match`; el botón principal de la cabecera cambia con la pestaña.
 */
export interface AccionFicha {
    texto: string;
    icono: NombreIcono;
    onAccion: () => void;
    ocupado?: boolean;
    ocupadoTexto?: string;
    inactiva?: boolean;
}

export interface DatosFicha {
    evento: Evento;
    version: string;
    tipos: TipoEntrada[];
    invitados: ResumenInvitados | null;
    cancelacion: Cancelacion | null;
}

export interface PropsPestana extends PropsEventos {
    datos: DatosFicha;
    sedes: readonly { id: string; nombre: string }[];
    permisos: PermisosEventos;
    form: FormEvento;
    setForm: (f: FormEvento) => void;
    errores: Record<string, string>;
    edita: boolean;
    recargarFicha: () => void;
    /** Pone el evento devuelto por la API (con su `ETag`) sin volver a leerlo. */
    ponerEvento: (e: Evento, version: string | null) => void;
    irPestana: (p: Pestana) => void;
    setAccion: (a: AccionFicha | null) => void;
    abrirTipo: (t: TipoEntrada | 'nuevo') => void;
}

export function Ficha(props: PropsEventos & { id: string; sedes: readonly { id: string; nombre: string }[]; permisos: PermisosEventos; onCambiado: () => void; onCerrar: () => void }) {
    const { api, negocio, id, movil, soloLectura, permisos, onCambiado, onCerrar, membresia, nombreNegocio, navegar } = props;
    const avisar = useAvisoBreve();
    const { puede } = usePermisos();
    const [datos, setDatos] = useState<DatosFicha | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [pestana, setPestana] = useState<Pestana>('datos');
    const [form, setFormEstado] = useState<FormEvento | null>(null);
    const [intentado, setIntentado] = useState(false);
    const [deApi, setDeApi] = useState<Record<string, string>>({});
    const [guardando, setGuardando] = useState(false);
    const [accionHija, setAccion] = useState<AccionFicha | null>(null);
    const [tipo, setTipo] = useState<TipoEntrada | 'nuevo' | null>(null);
    const recargarFicha = useCallback(() => { setIntento((i) => i + 1); }, []);

    useEffect(() => {
        let vivo = true;
        api.getEvent(negocio, id)
            .then(async (r) => {
                const e = r.datos;
                const [tipos, invitados, cancelacion] = await Promise.all([
                    api.listTicketTypes(negocio, id).then((l) => l.data).catch(() => [] as TipoEntrada[]),
                    permisos.veInvitados && e.sale_mode !== 'tickets' ? api.getEventGuestSummary(negocio, id).catch(() => null) : Promise.resolve(null),
                    e.status === 'cancelled' ? api.getEventCancellation(negocio, id).catch(() => null) : Promise.resolve(null),
                ]);
                if (!vivo) {
                    return;
                }
                setDatos({ evento: e, version: r.version ?? '', tipos, invitados, cancelacion });
                setFormEstado(formDe(e));
                setError(null);
            })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [api, negocio, id, intento, permisos.veInvitados]);

    const ponerEvento = useCallback((e: Evento, version: string | null) => {
        setDatos((d) => (d === null ? d : { ...d, evento: e, version: version ?? d.version }));
        setFormEstado(formDe(e));
        setDeApi({});
        setIntentado(false);
        onCambiado();
    }, [onCambiado]);

    const comun = { abierto: true, onCerrar, movil, tamano: 'ficha' as const, icono: 'ticket' as const };
    if (error !== null) {
        const est = estadoDeError(error);
        return (
            <PanelLateral {...comun} titulo="Evento" eyebrow="Evento">
                {est.tipo === 'sin-permiso' || (error instanceof ErrorApi && error.codigo === 'not_found') ? (
                    <EstadoSinPermiso seccion="este evento" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={null} movil={movil} />
                ) : (
                    <EstadoError seccion="el evento" codigoSoporte={est.codigoSoporte} onReintentar={recargarFicha} movil={movil} />
                )}
            </PanelLateral>
        );
    }
    if (datos === null || form === null) {
        return (
            <PanelLateral {...comun} titulo="Evento" eyebrow="Evento">
                <EstadoCargando seccion="el evento" />
            </PanelLateral>
        );
    }

    const e = datos.evento;
    const visibles = pestanasDe(e, permisos);
    const activa = visibles.includes(pestana) ? pestana : 'datos';
    const edita = editable(e, permisos.gestiona, soloLectura);
    const errores = { ...(intentado ? erroresForm(form, e) : {}), ...deApi };
    const cambios = cambiosDe(e, form);
    const hayCambios = Object.keys(cambios).length > 0;

    const guardar = () => {
        setIntentado(true);
        if (Object.keys(erroresForm(form, e)).length > 0) {
            return;
        }
        setGuardando(true);
        api.updateEvent(negocio, e.id, cambios, datos.version)
            .then((r) => {
                setGuardando(false);
                ponerEvento(r.datos, r.version);
                avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
            })
            .catch((err: unknown) => {
                setGuardando(false);
                const campos = erroresDeCampo(err);
                setDeApi(campos);
                if (err instanceof ErrorApi && err.codigo === 'version_conflict') {
                    recargarFicha();
                }
                if (Object.keys(campos).length === 0) {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) });
                }
            });
    };

    const props2: PropsPestana = {
        ...props,
        datos,
        form,
        setForm: (f) => { setFormEstado(f); setDeApi({}); },
        errores,
        edita,
        recargarFicha,
        ponerEvento,
        irPestana: (p) => { setAccion(null); setPestana(p); },
        setAccion,
        abrirTipo: setTipo,
    };

    let accion: AccionFicha | null = null;
    if (activa === 'datos' || activa === 'entradas' || activa === 'puerta') {
        if (edita && hayCambios) {
            accion = { texto: 'Guardar', icono: 'check', onAccion: guardar, ocupado: guardando, ocupadoTexto: 'Guardando…' };
        } else if (activa === 'entradas' && edita && e.sale_mode !== 'lists_only') {
            accion = { texto: 'Nuevo tipo de entrada', icono: 'plus', onAccion: () => { setTipo('nuevo'); } };
        }
    } else {
        accion = accionHija;
    }

    const [estado, tono] = ESTADO_EVENTO[e.status];
    const sede = props.sedes.find((s) => s.id === e.location_id)?.nombre ?? '';
    const contenido = {
        datos: <Datos {...props2} />,
        entradas: <Entradas {...props2} />,
        puerta: <Puerta {...props2} />,
        publicar: <Publicar {...props2} />,
        fecha: <CambiarFecha {...props2} />,
        cancelar: <Cancelar {...props2} />,
        informe: <Informe {...props2} />,
    }[activa];

    return (
        <>
            <PanelLateral
                {...comun}
                titulo={e.name ?? ''}
                eyebrow="Evento"
                subtitulo={[rangoEn(e.starts_at, e.ends_at, e.timezone), sede].filter((x) => x !== '').join(' · ')}
                {...(e.status === 'published' ? {} : { chip: estado, chipEstado: tono })}
                accion={accion?.texto ?? ''}
                accionIcono={accion?.icono ?? 'check'}
                ocupado={accion?.ocupado === true}
                ocupadoTexto={accion?.ocupadoTexto ?? 'Guardando…'}
                inactiva={accion?.inactiva === true}
                onAccion={accion?.onAccion}
            >
                <div className="rv-ev-ficha">
                    <div role="tablist" aria-label="Partes del evento" className={`rv-ev-pestanas${movil ? ' rv-ev-pestanas--movil' : ''}`}>
                        {visibles.map((p) => (
                            <button
                                key={p}
                                type="button"
                                role="tab"
                                aria-selected={p === activa}
                                className={`rv-ev-pestana${p === activa ? ' rv-ev-pestana--activa' : ''}`}
                                onClick={() => { setAccion(null); setPestana(p); }}
                            >
                                <Icono nombre={PESTANAS[p][1]} />
                                <span>{PESTANAS[p][0]}</span>
                            </button>
                        ))}
                    </div>
                    <div role="tabpanel" className="rv-ev-contenido" aria-label={PESTANAS[activa][0]}>
                        <details className="rv-ev-ayuda">
                            <summary>
                                <Icono nombre="circle-help" />
                                <span>Cómo funciona</span>
                            </summary>
                            {AYUDA[activa].map((a) => (
                                <p key={a.titulo}>
                                    <strong>{a.titulo}.</strong> {a.texto}
                                </p>
                            ))}
                        </details>
                        {!edita && (activa === 'datos' || activa === 'entradas' || activa === 'puerta') && permisos.gestiona && soloLectura && (
                            <p className="rv-ca-nota">
                                <Icono nombre="lock" />
                                <span>El negocio está en solo lectura: puedes consultar y cancelar el evento, pero no cambiarlo.</span>
                            </p>
                        )}
                        {contenido}
                        {(activa === 'datos' || activa === 'puerta') && <EnlacesDelEvento evento={e} puede={puede} navegar={navegar} invitados={datos.invitados} />}
                    </div>
                </div>
            </PanelLateral>
            {tipo !== null && (
                <FormTipo
                    {...props2}
                    tipo={tipo === 'nuevo' ? null : tipo}
                    onCerrar={(cambiado) => {
                        setTipo(null);
                        if (cambiado) {
                            recargarFicha();
                            onCambiado();
                        }
                    }}
                />
            )}
        </>
    );
}

function EnlacesDelEvento({ evento, puede, navegar, invitados }: { evento: Evento; puede: (c: string) => boolean; navegar: (r: string) => void; invitados: ResumenInvitados | null }) {
    const id = encodeURIComponent(evento.id);
    const enlaces = [
        puede('entradas.pedidos.ver') && evento.sale_mode !== 'lists_only' ? { titulo: 'Pedidos', texto: 'Pedidos y entradas del evento', icono: 'receipt' as const, ruta: `/pedidos?apartado=pedidos&evento=${id}` } : null,
        puede('entradas.informes.ver') ? { titulo: 'Informes', texto: 'Ventas, aforo y llegadas', icono: 'chart-line' as const, ruta: `/pedidos?apartado=informes&evento=${id}` } : null,
        puede('listas.listas.ver') && evento.sale_mode !== 'tickets'
            ? { titulo: 'Listas', texto: invitados === null ? 'Invitados de las listas' : `${String(invitados.active_guests)} invitados`, icono: 'list-checks' as const, ruta: `/listas?apartado=listas&evento=${id}` }
            : null,
    ].filter((x) => x !== null);
    if (enlaces.length === 0) {
        return null;
    }
    return (
        <section className="rv-ev-enlaces" aria-label="Del evento">
            <p className="rv-ev-ceja">Del evento</p>
            <div className="rv-ev-enlaces__fila">
                {enlaces.map((l) => (
                    <button key={l.titulo} type="button" className="rv-ev-enlace-tarjeta" onClick={() => { navegar(l.ruta); }}>
                        <span className="rv-ca-baldosa">
                            <Icono nombre={l.icono} />
                        </span>
                        <span className="rv-en-celda">
                            <strong>{l.titulo}</strong>
                            <small>{l.texto}</small>
                        </span>
                        <Icono nombre="arrow-right" />
                    </button>
                ))}
            </div>
        </section>
    );
}
